import type { NativeGitStatus } from '@crowdex/workspace-protocol';
import type { PullRequestCockpit } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { useCallback, useEffect, useState } from 'react';
import { PREVIEW_PULL_REQUEST_COCKPIT } from '@/lib/desktop-preview-data';
import { errorMessage } from '@/lib/error-message';
import { type ConversationPullRequestClaim, claimedPullRequest } from '@/lib/pull-request';
import { pullRequestForBranch, summarizePullRequestChecks } from './pull-request';
import { readPullRequestCockpit } from './pull-request-client';

/** What a session surface (header, summary, panel) knows of the session it shows. */
export type PullRequestSessionContext = {
  taskId: string;
  workspace: string | null;
  /** A cloud task reads its pull request through the task's routes, not a local checkout. */
  cloud: boolean;
  visible: boolean;
  preview: boolean;
  gitStatus: NativeGitStatus | null;
  /**
   * In a folder other conversations share, the pull request the server records the session's work
   * is on (null: none): the folder's branch alone makes no pull request its. Absent where the
   * folder is the session's own (a worktree of its own, a cloud task).
   */
  claim?: ConversationPullRequestClaim | null | undefined;
};

/** Running checks change on their own; the status is re-read while any is pending. */
export const PENDING_CHECKS_POLL_MS = 15_000;

/** The session's pull request: the live cockpit, or the one git status last saw for the branch. */
export function useSessionPullRequest(session: PullRequestSessionContext, enabled = true) {
  const [cockpit, setCockpit] = useState<PullRequestCockpit | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { taskId, workspace, cloud, preview, gitStatus: git } = session;
  const active = Boolean(enabled && session.visible && workspace);
  // Reload when the branch or its pull request changes.
  const version = `${git?.branch ?? ''}:${git?.pullRequest?.updatedAt ?? ''}`;
  const [poll, setPoll] = useState(0);
  const reload = useCallback(() => setPoll((value) => value + 1), []);
  const sessionKey = `${taskId}:${workspace ?? ''}:${cloud ? 'cloud' : 'local'}`;
  // Another session's pull request is never shown while this one's loads.
  useEffect(() => {
    void sessionKey;
    setCockpit(null);
    setError(null);
  }, [sessionKey]);
  // Each read with checks still pending reads again a while later.
  useEffect(() => {
    if (!active || preview || cockpit === null) return;
    if (summarizePullRequestChecks(cockpit).tone !== 'pending') return;
    const timer = setTimeout(() => setPoll((value) => value + 1), PENDING_CHECKS_POLL_MS);
    return () => clearTimeout(timer);
  }, [active, preview, cockpit]);
  useEffect(() => {
    void poll;
    void version;
    if (!active || !workspace) return;
    let current = true;
    setLoading(true);
    const load = preview
      ? Promise.resolve(PREVIEW_PULL_REQUEST_COCKPIT)
      : readPullRequestCockpit({ workspace, taskId, cloud });
    void load
      .then((value) => {
        if (!current) return;
        setCockpit(value);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (current) setError(errorMessage(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [active, cloud, preview, taskId, version, poll, workspace]);
  const found =
    cockpit?.pullRequest ??
    (cloud ? null : pullRequestForBranch(null, git?.pullRequest, git?.branch ?? null));
  if (session.claim === undefined) return { cockpit, pullRequest: found, loading, error, reload };
  // A shared folder: its branch's pull request only when it is the session's own.
  const pullRequest = claimedPullRequest(session.claim, found, null);
  const own = cockpit !== null && cockpit.pullRequest?.url === pullRequest?.url ? cockpit : null;
  return { cockpit: own, pullRequest, loading, error, reload };
}
